import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowRight, Award, Check, Clock3, RotateCcw, Sparkles, Trophy, X, Zap } from 'lucide-react';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import { challengeAPI } from '../services/api';
import XPToast from '../components/ui/XPToast';
import SpeakButton from '../components/ui/SpeakButton';

const DURATION = 60; // seconds

// ── Built-in fallback questions (used when Supabase quiz_questions is empty) ──
const FALLBACK_QUESTIONS = [
  { id: 'f1',  question_text: 'Welcher Artikel passt? ___ Hund bellt laut.', options: ['Der', 'Die', 'Das', 'Den'], correct_answer: 'Der' },
  { id: 'f2',  question_text: 'Welches Verb ist richtig? Er ___ gestern ins Kino gegangen.', options: ['ist', 'hat', 'sein', 'war'], correct_answer: 'ist' },
  { id: 'f3',  question_text: 'Wie heißt die Pluralform? Das Kind → die ___', options: ['Kindes', 'Kinder', 'Kinds', 'Kinden'], correct_answer: 'Kinder' },
  { id: 'f4',  question_text: 'Welche Präposition? Ich fahre ___ Berlin.', options: ['nach', 'zu', 'in', 'an'], correct_answer: 'nach' },
  { id: 'f5',  question_text: 'Welcher Kasus? Ich gebe ___ Mann das Buch.', options: ['dem', 'den', 'der', 'des'], correct_answer: 'dem' },
  { id: 'f6',  question_text: 'Konjugiere: Du ___ heute sehr müde. (sein)', options: ['bist', 'ist', 'sind', 'seid'], correct_answer: 'bist' },
  { id: 'f7',  question_text: 'Perfekt: Ich ___ das Buch gelesen.', options: ['habe', 'bin', 'hatte', 'war'], correct_answer: 'habe' },
  { id: 'f8',  question_text: 'Was ist das Gegenteil von "groß"?', options: ['klein', 'kurz', 'eng', 'dünn'], correct_answer: 'klein' },
  { id: 'f9',  question_text: 'Welcher Artikel? ___ Sonne scheint hell.', options: ['Die', 'Der', 'Das', 'Den'], correct_answer: 'Die' },
  { id: 'f10', question_text: 'Welche Form ist korrekt? Wir ___ morgen reisen.', options: ['werden', 'sind', 'haben', 'wollen'], correct_answer: 'werden' },
  { id: 'f11', question_text: 'Komparativ: schnell → ___', options: ['schneller', 'am schnellsten', 'schnellst', 'schnelle'], correct_answer: 'schneller' },
  { id: 'f12', question_text: 'Welche Konjunktion? Ich komme, ___ ich Zeit habe.', options: ['wenn', 'weil', 'obwohl', 'damit'], correct_answer: 'wenn' },
  { id: 'f13', question_text: 'Passiv: Das Buch ___ von Maria geschrieben.', options: ['wurde', 'hat', 'ist', 'war'], correct_answer: 'wurde' },
  { id: 'f14', question_text: 'Akkusativ: Ich sehe ___ Hund.', options: ['den', 'dem', 'der', 'des'], correct_answer: 'den' },
  { id: 'f15', question_text: 'Si thuhet "Dëshiroj" në gjermanisht?', options: ['Ich möchte', 'Ich will', 'Ich kann', 'Ich darf'], correct_answer: 'Ich möchte' },
  { id: 'f16', question_text: 'Superlativ: hoch → am ___', options: ['höchsten', 'hochsten', 'hohen', 'höhsten'], correct_answer: 'höchsten' },
  { id: 'f17', question_text: 'Reflexivpronomen: Er wäscht ___ die Hände.', options: ['sich', 'ihm', 'ihn', 'seiner'], correct_answer: 'sich' },
  { id: 'f18', question_text: 'Cili fjalë do të thotë "prandaj" në gjermanisht?', options: ['deshalb', 'obwohl', 'damit', 'falls'], correct_answer: 'deshalb' },
  { id: 'f19', question_text: 'Genitiv: Das Auto ___ Mannes ist rot.', options: ['des', 'dem', 'den', 'der'], correct_answer: 'des' },
  { id: 'f20', question_text: 'Konjunktiv II: Wenn ich Zeit hätte, ___ ich reisen.', options: ['würde', 'werde', 'will', 'kann'], correct_answer: 'würde' },
  { id: 'f21', question_text: 'Was ist die Vergangenheitsform von "gehen"?', options: ['ging', 'geht', 'gegangen', 'gehte'], correct_answer: 'ging' },
  { id: 'f22', question_text: 'Welche Präposition (Dativ)? Das Buch liegt ___ dem Tisch.', options: ['auf', 'an', 'in', 'über'], correct_answer: 'auf' },
  { id: 'f23', question_text: 'Welcher Artikel? ___ Wasser ist kalt.', options: ['Das', 'Der', 'Die', 'Den'], correct_answer: 'Das' },
  { id: 'f24', question_text: 'Trennbares Verb: Sie ___ das Licht ___. (anmachen)', options: ['macht … an', 'machen … auf', 'macht … auf', 'machte … an'], correct_answer: 'macht … an' },
  { id: 'f25', question_text: 'Modal: Er ___ morgen arbeiten. (müssen)', options: ['muss', 'musst', 'müsst', 'möchte'], correct_answer: 'muss' },
];

const TimedChallengePage = () => {
  const [phase, setPhase]       = useState('idle'); // idle | countdown | playing | done
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx]           = useState(0);
  const [answers, setAnswers]   = useState([]);
  const [timeLeft, setTimeLeft] = useState(DURATION);
  const [result, setResult]     = useState(null);
  const [xpToast, setXpToast]   = useState(null);
  const [selected, setSelected] = useState(null);
  const timerRef = useRef(null);
  const [bestScore, setBestScore] = useState(() => parseInt(localStorage.getItem('challengeBest') || '0'));



  const startGame = async () => {
    // Load questions first, then capture what was set
    let qs = [];
    try {
      const res = await challengeAPI.getQuestions();
      qs = res.data.questions || [];
    } catch { /* ignore */ }
    if (qs.length === 0) {
      qs = [...FALLBACK_QUESTIONS].sort(() => 0.5 - Math.random()).slice(0, 20);
    }
    setQuestions(qs);

    setPhase('countdown');
    let c = 3;
    setTimeLeft(c);
    const iv = setInterval(() => {
      c--;
      setTimeLeft(c);
      if (c <= 0) { clearInterval(iv); beginPlay(); }
    }, 1000);

  };

  const beginPlay = () => {
    setPhase('playing');
    setIdx(0);
    setAnswers([]);
    setSelected(null);
    setTimeLeft(DURATION);
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(timerRef.current); finishGame(); return 0; }
        return t - 1;
      });
    }, 1000);
  };

  const handleAnswer = (answer, correctAnswer) => {
    if (selected !== null) return;
    setSelected(answer);

    const current = questions[idx];
    const entry = { question_id: current.id, user_answer: answer, correct_answer: correctAnswer };
    const newAnswers = [...answers, entry];
    setAnswers(newAnswers);

    // Short delay then advance
    setTimeout(() => {
      if (idx + 1 >= questions.length) {
        clearInterval(timerRef.current);
        submitGame(newAnswers);
      } else {
        setIdx(i => i + 1);
        setSelected(null);
      }
    }, 600);
  };

  const submitGameRef = useRef(null);

  const submitGame = async (finalAnswers) => {
    setPhase('done');
    try {
      const elapsed = DURATION - timeLeft;
      const res = await challengeAPI.submit(finalAnswers, elapsed);
      setResult(res.data);
      if (res.data.xp_awarded > 0) setXpToast({ amount: res.data.xp_awarded, reason: 'timed_challenge' });
      const newBest = Math.max(bestScore, res.data.correct || 0);
      setBestScore(newBest);
      localStorage.setItem('challengeBest', newBest);
    } catch (e) { console.error(e); }
  };

  submitGameRef.current = submitGame;

  const finishGame = useCallback(() => {
    setAnswers(prev => {
      if (submitGameRef.current) submitGameRef.current(prev);
      return prev;
    });
  }, []);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const current = questions[idx];
  const urgentTime = timeLeft <= 10 && phase === 'playing';

  return (
    <DashboardLayout>
      <main className="mx-auto max-w-[1120px] space-y-7 px-1 pb-10 sm:px-3">
        <header className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <p className="mb-2 flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.18em] text-[#dd0000]">
              <Zap size={14} /> Ushtrim i shpejtë
            </p>
            <h1 className="text-3xl font-extrabold tracking-tight text-[#171717] sm:text-4xl">Sfida e kohës</h1>
            <p className="mt-2 text-sm text-zinc-500">Testo njohuritë e tua në gjermanisht brenda 60 sekondash.</p>
          </div>
          <div className="inline-flex w-fit items-center gap-2 rounded-xl border border-[#e3e3e3] bg-white px-3.5 py-2.5 text-sm text-zinc-600 shadow-sm">
            <Trophy size={17} className="text-[#947600]" />
            <span>Rekordi yt:</span>
            <strong className="font-extrabold text-[#171717]">{bestScore}</strong>
          </div>
        </header>

        {phase === 'idle' && (
          <section className="overflow-hidden rounded-3xl border border-[#e3e3e3] bg-white shadow-[0_8px_24px_rgba(0,0,0,0.05)]">
            <div aria-hidden="true" className="flex h-1.5">
              <span className="flex-1 bg-zinc-950" />
              <span className="flex-1 bg-[#dd0000]" />
              <span className="flex-1 bg-[#ffce00]" />
            </div>
            <div className="grid md:grid-cols-[minmax(0,1fr)_280px]">
              <div className="p-6 sm:p-10">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#fff0f0] text-[#dd0000]">
                  <Zap size={24} />
                </span>
                <p className="mt-6 text-xs font-extrabold uppercase tracking-[0.16em] text-[#dd0000]">Sfida ditore</p>
                <h2 className="mt-2 text-2xl font-extrabold tracking-tight text-[#171717] sm:text-3xl">Sa shumë di brenda një minute?</h2>
                <p className="mt-3 max-w-xl text-sm leading-6 text-zinc-500">
                  Përgjigju sa më shumë pyetjeve të gramatikës gjermane. Çdo përgjigje e saktë të sjell 5 XP.
                </p>
                <button
                  type="button"
                  onClick={startGame}
                  className="mt-7 inline-flex items-center gap-2 rounded-xl bg-[#171717] px-5 py-3 text-sm font-extrabold text-white shadow-sm transition hover:bg-[#dd0000] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#dd0000] focus-visible:ring-offset-2"
                >
                  Fillo sfidën <ArrowRight size={16} />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-3 border-t border-[#eeeae3] bg-[#fbfaf7] p-5 md:grid-cols-1 md:border-l md:border-t-0 md:p-7">
                <div className="rounded-2xl border border-[#e9e4dc] bg-white p-3.5 sm:p-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#fff8d9] text-[#8b7000]"><Clock3 size={18} /></span>
                  <strong className="mt-3 block text-lg font-extrabold text-[#171717]">60 sek.</strong>
                  <span className="text-xs text-zinc-500">Kohë për sfidën</span>
                </div>
                <div className="rounded-2xl border border-[#e9e4dc] bg-white p-3.5 sm:p-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#fff0f0] text-[#dd0000]"><Sparkles size={18} /></span>
                  <strong className="mt-3 block text-lg font-extrabold text-[#171717]">+5 XP</strong>
                  <span className="text-xs text-zinc-500">Për çdo përgjigje të saktë</span>
                </div>
                <div className="rounded-2xl border border-[#e9e4dc] bg-white p-3.5 sm:p-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#f1f1f1] text-[#171717]"><Award size={18} /></span>
                  <strong className="mt-3 block text-lg font-extrabold text-[#171717]">Rekord personal</strong>
                  <span className="text-xs text-zinc-500">Përmirësoje rezultatin</span>
                </div>
              </div>
            </div>
          </section>
        )}

        {phase === 'countdown' && (
          <section className="flex min-h-[360px] flex-col items-center justify-center rounded-3xl border border-[#e3e3e3] bg-white text-center shadow-[0_8px_24px_rgba(0,0,0,0.05)]">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#fff0f0] text-[#dd0000]"><Zap size={26} /></span>
            <p className="mt-5 text-xs font-extrabold uppercase tracking-[0.18em] text-zinc-500">Bëhu gati</p>
            <div className="mt-2 text-8xl font-black tabular-nums tracking-tight text-[#171717] animate-pulse">{timeLeft || '🚀'}</div>
          </section>
        )}

        {phase === 'playing' && current && (
          <section className="mx-auto w-full max-w-3xl space-y-4">
            <div className="rounded-2xl border border-[#e3e3e3] bg-white p-4 shadow-sm sm:p-5">
              <div className="mb-3 flex items-center justify-between gap-4">
                <span className="text-xs font-bold text-zinc-500">Pyetja {idx + 1} nga {questions.length}</span>
                <span className={`inline-flex min-w-[82px] items-center justify-center gap-1.5 rounded-xl px-3 py-1.5 text-sm font-extrabold tabular-nums ${urgentTime ? 'bg-red-50 text-[#dd0000]' : 'bg-[#fff8d9] text-[#725c00]'}`}>
                  <Clock3 size={15} className={urgentTime ? 'animate-pulse' : ''} />
                  00:{String(timeLeft).padStart(2, '0')}
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-zinc-100">
                <div
                  className={`h-full rounded-full transition-all duration-1000 ${urgentTime ? 'bg-[#dd0000]' : 'bg-[#ffce00]'}`}
                  style={{ width: `${(timeLeft / DURATION) * 100}%` }}
                />
              </div>
            </div>

            <div className="rounded-3xl border border-[#e3e3e3] bg-white p-5 shadow-[0_8px_24px_rgba(0,0,0,0.05)] sm:p-8">
              <div className="mb-6 flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-extrabold uppercase tracking-[0.15em] text-[#dd0000]">Zgjidh përgjigjen e saktë</p>
                  <h2 className="mt-3 text-lg font-bold leading-relaxed text-[#171717] sm:text-xl">{current.question_text}</h2>
                </div>
                <SpeakButton text={current.question_text} lang="de-DE" />
              </div>

              <div className="grid gap-2.5">
                {current.options.length > 0
                  ? current.options.map((opt, optionIndex) => {
                      const isSelected = selected === opt;
                      const isCorrect = opt === current.correct_answer;
                      let optionStyle = 'border-zinc-200 bg-white text-zinc-700 hover:border-[#dd0000]/40 hover:bg-[#fffafa]';
                      let statusIcon = null;
                      if (selected !== null) {
                        if (isCorrect) {
                          optionStyle = 'border-emerald-300 bg-emerald-50 text-emerald-800';
                          statusIcon = <Check size={17} />;
                        } else if (isSelected) {
                          optionStyle = 'border-red-200 bg-red-50 text-red-700';
                          statusIcon = <X size={17} />;
                        } else {
                          optionStyle = 'border-zinc-100 bg-zinc-50 text-zinc-400 opacity-65';
                        }
                      }
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => handleAnswer(opt, current.correct_answer)}
                          disabled={selected !== null}
                          className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3.5 text-left text-sm font-semibold transition disabled:cursor-default ${optionStyle}`}
                        >
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-zinc-100 text-[11px] font-extrabold text-zinc-500">
                            {String.fromCharCode(65 + optionIndex)}
                          </span>
                          <span className="flex-1">{opt}</span>
                          {statusIcon}
                        </button>
                      );
                    })
                  : (
                    <input
                      type="text"
                      autoFocus
                      placeholder="Shkruaj përgjigjen..."
                      className="h-12 rounded-xl border border-zinc-200 bg-white px-4 text-sm text-zinc-900 outline-none transition placeholder:text-zinc-400 focus:border-[#dd0000] focus:ring-2 focus:ring-red-100"
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') handleAnswer(event.target.value, current.correct_answer);
                      }}
                    />
                  )}
              </div>
            </div>
          </section>
        )}

        {phase === 'done' && result && (
          <section className="mx-auto w-full max-w-2xl overflow-hidden rounded-3xl border border-[#e3e3e3] bg-white text-center shadow-[0_8px_24px_rgba(0,0,0,0.05)]">
            <div aria-hidden="true" className="flex h-1.5">
              <span className="flex-1 bg-zinc-950" />
              <span className="flex-1 bg-[#dd0000]" />
              <span className="flex-1 bg-[#ffce00]" />
            </div>
            <div className="p-7 sm:p-10">
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#fff8d9] text-[#8b7000]">
                <Trophy size={30} />
              </span>
              <p className="mt-5 text-xs font-extrabold uppercase tracking-[0.17em] text-[#dd0000]">Sfida e përfunduar</p>
              <h2 className="mt-2 text-2xl font-extrabold tracking-tight text-[#171717]">Punë e shkëlqyer!</h2>
              <div className="mt-6 flex items-center justify-center gap-3">
                <strong className="text-5xl font-black tracking-tight text-[#171717]">{result.correct}</strong>
                <span className="text-xl font-bold text-zinc-300">/</span>
                <span className="text-2xl font-bold text-zinc-500">{result.total}</span>
                <span className="ml-1 text-sm font-semibold text-zinc-500">të sakta</span>
              </div>
              <div className="mx-auto mt-5 flex max-w-sm items-center justify-center gap-3">
                <div className="flex-1 rounded-xl bg-[#fbfaf7] px-4 py-3">
                  <span className="block text-xl font-extrabold text-[#171717]">{result.score}%</span>
                  <span className="text-[11px] font-semibold text-zinc-500">Saktësia</span>
                </div>
                <div className="flex-1 rounded-xl bg-[#fff8d9] px-4 py-3">
                  <span className="block text-xl font-extrabold text-[#725c00]">+{result.xp_awarded || 0} XP</span>
                  <span className="text-[11px] font-semibold text-zinc-500">Pikë përvoje</span>
                </div>
              </div>
              {result.correct > bestScore && (
                <p className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#fff8d9] px-3 py-1.5 text-xs font-bold text-[#725c00]">
                  <Sparkles size={14} /> Rekord i ri personal!
                </p>
              )}
              <div>
                <button
                  type="button"
                  onClick={() => setPhase('idle')}
                  className="mt-7 inline-flex items-center gap-2 rounded-xl bg-[#171717] px-5 py-3 text-sm font-extrabold text-white transition hover:bg-[#dd0000] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#dd0000] focus-visible:ring-offset-2"
                >
                  <RotateCcw size={15} /> Provo përsëri
                </button>
              </div>
            </div>
          </section>
        )}

        {xpToast && <XPToast amount={xpToast.amount} reason={xpToast.reason} onDone={() => setXpToast(null)} />}
      </main>
    </DashboardLayout>
  );
};

export default TimedChallengePage;
